import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  decodeReflection,
  type PlayerResponseSignal,
} from "@/features/feedback/responseSignals";
import type {
  CoachEngagementFilters,
  CoachEngagement,
  EngagementListItem,
  EngagementMatchRow,
  EngagementPlayerRow,
  EngagementPlaylistRow,
  EngagementReflectionItem,
  Player,
} from "@/lib/types";
import { useMatches } from "@/features/matches/useMatches";
import { useRoster } from "./hooks/useRoster";
import { useCoachPlaylistsList } from "./hooks/useCoachPlaylistsList";
import { useCoachEngagement } from "./hooks/useCoachEngagement";
import { PageHeader } from "./components/PageHeader";
import { TkticDateInput } from "@/components/ui/TkticDateInput";
import {
  TkticSelect,
  type TkticSelectOption,
} from "@/components/ui/TkticSelect";

/**
 * Coach > Engagement dashboard (M6a).
 *
 * Ports the legacy `renderCoachEngagementDashboard`
 * (`js/coaching/engagement.js`): a filter bar + five KPI tiles +
 * three breakdown tables (by player / playlist / match) + four
 * follow-up lists (unreviewed assigned items, reflections needing
 * response, players with no recent feedback, most watched).
 *
 * Filter-driven: the filter bar's six controls (player · playlist
 * · match · visibility · start date · end date) feed a `filters`
 * object that's the query key for `useCoachEngagement` — changing
 * a filter refetches.
 *
 * The dashboard's `limitations` field is surfaced as caveat notes
 * (e.g. "clip watch tracking is not yet supported") so the coach
 * knows what the numbers do and don't cover.
 */

const VISIBILITY_FILTER_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "", label: "All visibilities" },
  { value: "player", label: "Player + family" },
  { value: "team", label: "Team" },
];

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function EngagementPage() {
  const navigate = useNavigate({ from: "/coach" });
  // ---- Filter state ----
  // Held as raw strings (the controls' values); converted to the
  // typed `CoachEngagementFilters` for the query. An invalid date
  // is simply not forwarded — the query ignores it rather than
  // 422-ing.
  const [playerId, setPlayerId] = useState("");
  const [playlistId, setPlaylistId] = useState("");
  const [matchId, setMatchId] = useState("");
  const [visibility, setVisibility] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const filters: CoachEngagementFilters = useMemo(() => {
    const f: CoachEngagementFilters = {};
    if (playerId) f.player_id = playerId;
    if (playlistId) f.playlist_id = Number(playlistId);
    if (matchId) f.match_id = matchId;
    if (visibility) f.visibility = visibility;
    if (startDate && DATE_RE.test(startDate)) f.start_date = startDate;
    if (endDate && DATE_RE.test(endDate)) f.end_date = endDate;
    return f;
  }, [playerId, playlistId, matchId, visibility, startDate, endDate]);

  // Filter-option sources.
  const rosterQuery = useRoster();
  const playlistsQuery = useCoachPlaylistsList();
  const matchesQuery = useMatches();
  const players = rosterQuery.data?.players ?? [];
  const playlists = playlistsQuery.data?.playlists ?? [];
  const matches = matchesQuery.data ?? [];

  const engagementQuery = useCoachEngagement(filters);
  const data = engagementQuery.data;
  const followUpGroups = useMemo(
    () =>
      data
        ? buildFollowUpGroups({
            data,
            players,
            onOpenPlayer: (playerId, context) => {
              void navigate({
                to: "/coach",
                search: { tab: "roster", player_id: playerId, context },
              });
            },
            onOpenNotes: () => {
              void navigate({
                to: "/coach",
                search: { tab: "library", library: "notes" },
              });
            },
            onOpenSessions: () => {
              void navigate({
                to: "/coach",
                search: { tab: "library", library: "sessions" },
              });
            },
          })
        : [],
    [data, navigate, players],
  );

  return (
    <div className="flex flex-col gap-5" data-testid="engagement-page">
      <PageHeader
        title="Follow-up Intelligence"
        description="Action-first review signals for deciding who needs a reply, a nudge, or a simpler next step. Private note bodies and coach-only notes are not shown here."
        actions={
          <button
            type="button"
            onClick={() => engagementQuery.refetch()}
            disabled={engagementQuery.isFetching}
            data-testid="engagement-refresh"
            className="border border-rule text-ink hover:bg-surface disabled:opacity-50 rounded px-4 py-1.5 text-sm font-display uppercase tracking-wide transition-colors"
          >
            {engagementQuery.isFetching ? "Refreshing…" : "Refresh"}
          </button>
        }
      />

      {/* Filter bar — six controls. */}
      <div
        className="flex flex-wrap gap-3 rounded-lg border border-rule bg-bg-card p-3"
        data-testid="engagement-filters"
      >
        <FilterSelect
          label="Player"
          value={playerId}
          onChange={setPlayerId}
          testid="engagement-filter-player"
          options={[
            { value: "", label: "All players" },
            ...players.map((p) => ({
              value: p.id,
              label: p.jersey_number
                ? `#${p.jersey_number} ${p.display_name}`
                : p.display_name,
            })),
          ]}
        />
        <FilterSelect
          label="Review session"
          value={playlistId}
          onChange={setPlaylistId}
          testid="engagement-filter-playlist"
          options={[
            { value: "", label: "All sessions" },
            ...playlists.map((pl) => ({
              value: String(pl.id),
              label: pl.title || `Session ${pl.id}`,
            })),
          ]}
        />
        <FilterSelect
          label="Match"
          value={matchId}
          onChange={setMatchId}
          testid="engagement-filter-match"
          options={[
            { value: "", label: "All matches" },
            ...matches.map((m) => ({
              value: m.id,
              label: `${m.home_team} vs ${m.away_team}${
                m.date ? ` · ${m.date}` : ""
              }`,
            })),
          ]}
        />
        <FilterSelect
          label="Visibility"
          value={visibility}
          onChange={setVisibility}
          testid="engagement-filter-visibility"
          options={VISIBILITY_FILTER_OPTIONS.map((o) => ({
            value: o.value,
            label: o.label,
          }))}
        />
        <FilterDate
          label="Start date"
          value={startDate}
          onChange={setStartDate}
          testid="engagement-filter-start"
        />
        <FilterDate
          label="End date"
          value={endDate}
          onChange={setEndDate}
          testid="engagement-filter-end"
        />
      </div>

      {engagementQuery.isLoading ? (
        <p
          className="text-ink-muted text-sm font-mono px-3 py-6"
          data-testid="engagement-loading"
        >
          Loading engagement dashboard…
        </p>
      ) : engagementQuery.isError || !data ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="engagement-error"
        >
          Could not load the engagement dashboard.
        </p>
      ) : (
        <>
          <FollowUpActionGrid groups={followUpGroups} />

          {/* KPI tiles. */}
          <div
            className="grid grid-cols-2 gap-3 lg:grid-cols-5"
            data-testid="engagement-kpis"
          >
            <KpiTile
              label="Assigned items"
              value={String(data.summary.assigned_items)}
            />
            <KpiTile
              label="Reviewed items"
              value={String(data.summary.reviewed_items)}
            />
            <KpiTile
              label="Completion"
              value={`${data.summary.completion_percentage}%`}
            />
            <KpiTile
              label="Reflections"
              value={String(data.summary.reflection_count)}
            />
            <KpiTile
              label="Latest review"
              value={data.summary.latest_reviewed_at ?? "—"}
            />
          </div>

          {/* Caveats — the dashboard's `limitations`. */}
          {data.limitations &&
          Object.keys(data.limitations).length > 0 ? (
            <p
              className="text-ink-muted text-xs"
              data-testid="engagement-limitations"
            >
              {Object.values(data.limitations)
                .filter((v) => typeof v === "string")
                .join(" · ")}
            </p>
          ) : null}

          <section className="flex flex-col gap-3" aria-label="Engagement diagnostic detail">
            <div>
              <h3 className="font-display text-sm text-ink">
                Diagnostic detail
              </h3>
              <p className="mt-1 text-xs text-ink-muted">
                Completion tables stay here for deeper analysis after the
                follow-up queue points you toward the next coach action.
              </p>
            </div>
            <PlayerBreakdownTable rows={data.by_player} />
            <PlaylistBreakdownTable rows={data.by_playlist} />
            <MatchBreakdownTable rows={data.by_match} />
          </section>

          {/* Follow-up lists. */}
          <div className="grid gap-3 lg:grid-cols-2">
            <FollowUpList
              title="Unreviewed assigned items"
              testid="engagement-unreviewed"
              emptyLabel="No unreviewed assigned items for these filters."
              items={data.unreviewed_assigned_items.map((i) => ({
                key: `${i.kind}-${i.item_id}`,
                primary: i.title || `${i.kind} ${i.item_id}`,
                meta: `${i.kind}${i.date ? ` · ${i.date}` : ""}`,
              }))}
            />
            <FollowUpList
              title="Reflections needing response"
              testid="engagement-reflections"
              emptyLabel="No player reflections for these filters."
              items={data.reflections_needing_response.map(
                (r: EngagementReflectionItem, idx) => ({
                  key: `reflection-${idx}`,
                  primary: r.reviewed_at ?? "Reflection",
                  meta: r.reflection,
                }),
              )}
            />
            <FollowUpList
              title="Players with no recent feedback"
              testid="engagement-no-recent"
              emptyLabel="Every active filtered player has recent feedback."
              items={data.players_with_no_recent_feedback.map((p) => ({
                key: p.player_id,
                primary: p.jersey_number
                  ? `#${p.jersey_number} ${p.display_name}`
                  : p.display_name,
                meta: "No feedback in the recent window.",
              }))}
            />
            <FollowUpList
              title="Most watched"
              testid="engagement-most-watched"
              emptyLabel="No review activity yet."
              items={data.most_watched.map((i: EngagementListItem) => ({
                key: `${i.kind}-${i.item_id}`,
                primary: i.title || `${i.kind} ${i.item_id}`,
                meta: `${i.review_count ?? 0} review${
                  (i.review_count ?? 0) === 1 ? "" : "s"
                } · ${i.kind}`,
              }))}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ---- Action-first follow-up queue ----

type PlayerArrivalContext =
  | "need_help"
  | "tried_this"
  | "no_recent_feedback"
  | "unreviewed_feedback";

type EngagementFollowUpTone = "default" | "good" | "flag" | "accent";

type EngagementFollowUpItem = {
  key: string;
  primary: string;
  meta: string;
  cta: string;
  tone: EngagementFollowUpTone;
  onClick: () => void;
};

type EngagementFollowUpGroup = {
  title: string;
  testid: string;
  emptyLabel: string;
  items: EngagementFollowUpItem[];
};

function buildFollowUpGroups({
  data,
  players,
  onOpenPlayer,
  onOpenNotes,
  onOpenSessions,
}: {
  data: CoachEngagement;
  players: Player[];
  onOpenPlayer: (playerId: string, context: PlayerArrivalContext) => void;
  onOpenNotes: () => void;
  onOpenSessions: () => void;
}): EngagementFollowUpGroup[] {
  const needHelpItems: EngagementFollowUpItem[] = [];
  const triedThisItems: EngagementFollowUpItem[] = [];

  for (const player of players) {
    const needHelp = player.signal_summary?.need_help ?? 0;
    const triedThis = player.signal_summary?.tried_this ?? 0;
    if (needHelp > 0) {
      needHelpItems.push({
        key: `need-help-${player.id}`,
        primary: playerLabel(player),
        meta: `${needHelp} need-help response${needHelp === 1 ? "" : "s"}`,
        cta: "Open player",
        tone: "flag",
        onClick: () => onOpenPlayer(player.id, "need_help"),
      });
    }
    if (triedThis > 0) {
      triedThisItems.push({
        key: `tried-this-${player.id}`,
        primary: playerLabel(player),
        meta: `${triedThis} tried-this response${triedThis === 1 ? "" : "s"}`,
        cta: "Open player",
        tone: "good",
        onClick: () => onOpenPlayer(player.id, "tried_this"),
      });
    }
  }

  for (const [idx, reflection] of data.reflections_needing_response.entries()) {
    const decoded = decodeReflection(reflection.reflection);
    const signal = decoded.signal;
    if (signal !== "need_help" && signal !== "tried_this") continue;
    const target = signal === "need_help" ? needHelpItems : triedThisItems;
    target.push({
      key: `reflection-${reflection.note_id ?? reflection.playlist_id ?? idx}`,
      primary: signalLabel(signal),
      meta: decoded.note || reflection.reviewed_at || "Player response",
      cta: reflection.playlist_id ? "Open sessions" : "Open notes",
      tone: signal === "need_help" ? "flag" : "good",
      onClick: reflection.playlist_id ? onOpenSessions : onOpenNotes,
    });
  }

  const noResponseItems = data.by_player
    .filter((row) => row.reviewed_count > 0 && row.reflection_count === 0)
    .slice(0, 6)
    .map((row): EngagementFollowUpItem => ({
      key: `no-response-${row.player_id}`,
      primary: playerLabel(row),
      meta: `${row.reviewed_count} reviewed · no response signal yet`,
      cta: "Open player",
      tone: "default",
      onClick: () => onOpenPlayer(row.player_id, "unreviewed_feedback"),
    }));

  const notReviewedItems = data.unreviewed_assigned_items
    .slice(0, 6)
    .map((item): EngagementFollowUpItem => {
      const playerId = item.player_ids?.[0];
      return {
        key: `not-reviewed-${item.kind}-${item.item_id}`,
        primary: item.title || fallbackItemTitle(item),
        meta: [
          item.kind === "playlist" ? "review session" : item.kind,
          item.date,
          item.player_ids?.length
            ? `${item.player_ids.length} player${item.player_ids.length === 1 ? "" : "s"}`
            : "",
        ]
          .filter(Boolean)
          .join(" · "),
        cta: playerId
          ? "Open player"
          : item.kind === "playlist"
            ? "Open sessions"
            : "Open notes",
        tone: "accent",
        onClick: playerId
          ? () => onOpenPlayer(playerId, "unreviewed_feedback")
          : item.kind === "playlist"
            ? onOpenSessions
            : onOpenNotes,
      };
    });

  const noRecentItems = data.players_with_no_recent_feedback
    .slice(0, 6)
    .map((row): EngagementFollowUpItem => ({
      key: `no-recent-${row.player_id}`,
      primary: playerLabel(row),
      meta: "No recent player-facing feedback",
      cta: "Open player",
      tone: "flag",
      onClick: () => onOpenPlayer(row.player_id, "no_recent_feedback"),
    }));

  const incompleteSessionItems = data.by_playlist
    .filter((row) => row.assigned_count > row.reviewed_count)
    .slice(0, 6)
    .map((row): EngagementFollowUpItem => ({
      key: `session-incomplete-${row.playlist_id}`,
      primary: row.title || `Session ${row.playlist_id}`,
      meta: `${row.assigned_count - row.reviewed_count} not reviewed · ${row.completion_percentage}% complete`,
      cta: "Open sessions",
      tone: "accent",
      onClick: onOpenSessions,
    }));

  return [
    {
      title: "Need help",
      testid: "engagement-action-need-help",
      emptyLabel: "No need-help responses in the current filters.",
      items: needHelpItems.slice(0, 6),
    },
    {
      title: "Tried this",
      testid: "engagement-action-tried-this",
      emptyLabel: "No tried-this responses in the current filters.",
      items: triedThisItems.slice(0, 6),
    },
    {
      title: "No response",
      testid: "engagement-action-no-response",
      emptyLabel: "No reviewed players are missing response signals.",
      items: noResponseItems,
    },
    {
      title: "Not reviewed",
      testid: "engagement-action-not-reviewed",
      emptyLabel: "No assigned feedback is waiting on review.",
      items: notReviewedItems,
    },
    {
      title: "No recent feedback",
      testid: "engagement-action-no-recent",
      emptyLabel: "Every active player has a recent feedback touch.",
      items: noRecentItems,
    },
    {
      title: "Sessions incomplete",
      testid: "engagement-action-sessions-incomplete",
      emptyLabel: "All assigned review sessions are complete.",
      items: incompleteSessionItems,
    },
  ];
}

function FollowUpActionGrid({
  groups,
}: {
  groups: EngagementFollowUpGroup[];
}) {
  return (
    <section
      className="flex flex-col gap-3"
      aria-label="Follow-up action queue"
      data-testid="engagement-action-queue"
    >
      <div>
        <h3 className="font-display text-sm text-ink">
          Follow-up queue
        </h3>
        <p className="mt-1 text-xs text-ink-muted">
          Start here when you need to decide who needs a reply, reminder, or
          fresh piece of feedback.
        </p>
      </div>
      <div className="grid gap-3 lg:grid-cols-3">
        {groups.map((group) => (
          <FollowUpActionSection key={group.testid} group={group} />
        ))}
      </div>
    </section>
  );
}

function FollowUpActionSection({
  group,
}: {
  group: EngagementFollowUpGroup;
}) {
  return (
    <section
      className="rounded-lg border border-rule bg-bg-card p-3"
      data-testid={group.testid}
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-display text-ink">{group.title}</h4>
        <span className="rounded bg-surface px-2 py-0.5 text-[0.65rem] font-mono text-ink-muted">
          {group.items.length}
        </span>
      </div>
      {group.items.length === 0 ? (
        <p className="mt-3 text-xs text-ink-muted">{group.emptyLabel}</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {group.items.map((item) => (
            <li
              key={item.key}
              className={[
                "rounded border bg-surface px-2 py-2",
                followUpToneClass(item.tone),
              ].join(" ")}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-display text-ink">
                  {item.primary}
                </p>
                <p className="mt-0.5 line-clamp-2 text-xs text-ink-muted">
                  {item.meta}
                </p>
              </div>
              <button
                type="button"
                onClick={item.onClick}
                className="mt-2 rounded border border-rule px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider text-ink transition-colors hover:bg-bg-card"
              >
                {item.cta}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function followUpToneClass(tone: EngagementFollowUpTone): string {
  if (tone === "flag") return "border-flag/40";
  if (tone === "good") return "border-good/40";
  if (tone === "accent") return "border-accent/40";
  return "border-rule/60";
}

function playerLabel(player: {
  display_name: string;
  jersey_number?: string | null;
}): string {
  return player.jersey_number
    ? `#${player.jersey_number} ${player.display_name}`
    : player.display_name;
}

function signalLabel(signal: PlayerResponseSignal): string {
  if (signal === "need_help") return "Need-help response";
  if (signal === "tried_this") return "Tried-this response";
  return "Player response";
}

function fallbackItemTitle(item: EngagementListItem): string {
  const label = item.kind === "playlist" ? "review session" : item.kind;
  return `${label} ${item.item_id}`;
}

// ---- Filter controls ----

function FilterSelect(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
  options: TkticSelectOption[];
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      <TkticSelect
        value={props.value}
        onChange={props.onChange}
        data-testid={props.testid}
        ariaLabel={props.label}
        options={props.options}
      />
    </label>
  );
}

function FilterDate(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  testid: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
        {props.label}
      </span>
      <TkticDateInput
        value={props.value}
        onChange={props.onChange}
        data-testid={props.testid}
      />
    </label>
  );
}

// ---- KPI tile ----

function KpiTile(props: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-rule bg-bg-card px-3 py-2">
      <div className="text-ink-muted text-[0.65rem] font-display uppercase tracking-wider">
        {props.label}
      </div>
      <strong className="mt-0.5 block text-xl font-display text-ink tabular-nums">
        {props.value}
      </strong>
    </div>
  );
}

// ---- Breakdown tables ----

function BreakdownCard(props: {
  title: string;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="rounded-lg border border-rule bg-bg-card p-3"
      data-testid={props.testid}
    >
      <h3 className="text-sm font-display text-ink mb-2">
        {props.title}
      </h3>
      <div>{props.children}</div>
    </section>
  );
}

const TH = "px-2 py-1 text-left text-xs font-display uppercase tracking-wider text-ink-muted";
const TD = "px-2 py-1 text-sm text-ink tabular-nums";

function PlayerBreakdownTable({ rows }: { rows: EngagementPlayerRow[] }) {
  return (
    <BreakdownCard
      title="Review completion by player"
      testid="engagement-by-player"
    >
      <table className="hidden w-full sm:table">
        <thead>
          <tr className="border-b border-rule">
            <th className={TH}>Player</th>
            <th className={TH}>Assigned</th>
            <th className={TH}>Reviewed</th>
            <th className={TH}>Complete</th>
            <th className={TH}>Latest</th>
            <th className={TH}>Reflections</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={TD} colSpan={6}>
                No assigned feedback matches these filters.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={r.player_id}
                className="border-b border-rule/50"
              >
                <td className={`${TD} font-display`}>
                  {r.jersey_number
                    ? `#${r.jersey_number} ${r.display_name}`
                    : r.display_name}
                </td>
                <td className={TD}>{r.assigned_count}</td>
                <td className={TD}>{r.reviewed_count}</td>
                <td className={TD}>{r.completion_percentage}%</td>
                <td className={TD}>{r.latest_reviewed_at ?? "—"}</td>
                <td className={TD}>{r.reflection_count}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="flex flex-col gap-2 sm:hidden">
        {rows.length === 0 ? (
          <p className="text-sm text-ink-muted">No assigned feedback matches these filters.</p>
        ) : (
          rows.map((r) => (
            <EngagementMobileRow
              key={r.player_id}
              title={r.jersey_number ? `#${r.jersey_number} ${r.display_name}` : r.display_name}
              stats={[
                ["Assigned", r.assigned_count],
                ["Reviewed", r.reviewed_count],
                ["Complete", `${r.completion_percentage}%`],
                ["Reflections", r.reflection_count],
              ]}
              footer={r.latest_reviewed_at ? `Latest ${r.latest_reviewed_at}` : "No reviews yet"}
            />
          ))
        )}
      </div>
    </BreakdownCard>
  );
}

function PlaylistBreakdownTable({
  rows,
}: {
  rows: EngagementPlaylistRow[];
}) {
  return (
    <BreakdownCard
      title="Review completion by session"
      testid="engagement-by-playlist"
    >
      <table className="hidden w-full sm:table">
        <thead>
          <tr className="border-b border-rule">
            <th className={TH}>Review session</th>
            <th className={TH}>Assigned</th>
            <th className={TH}>Reviewed</th>
            <th className={TH}>Complete</th>
            <th className={TH}>Latest</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={TD} colSpan={5}>
                No review sessions match these filters.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={r.playlist_id}
                className="border-b border-rule/50"
              >
                <td className={`${TD} font-display`}>
                  {r.title || `Session ${r.playlist_id}`}
                </td>
                <td className={TD}>{r.assigned_count}</td>
                <td className={TD}>{r.reviewed_count}</td>
                <td className={TD}>{r.completion_percentage}%</td>
                <td className={TD}>{r.latest_reviewed_at ?? "—"}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="flex flex-col gap-2 sm:hidden">
        {rows.length === 0 ? (
          <p className="text-sm text-ink-muted">
            No review sessions match these filters.
          </p>
        ) : (
          rows.map((r) => (
            <EngagementMobileRow
              key={r.playlist_id}
              title={r.title || `Session ${r.playlist_id}`}
              stats={[
                ["Assigned", r.assigned_count],
                ["Reviewed", r.reviewed_count],
                ["Complete", `${r.completion_percentage}%`],
              ]}
              footer={r.latest_reviewed_at ? `Latest ${r.latest_reviewed_at}` : "No reviews yet"}
            />
          ))
        )}
      </div>
    </BreakdownCard>
  );
}

function MatchBreakdownTable({ rows }: { rows: EngagementMatchRow[] }) {
  return (
    <BreakdownCard
      title="Review completion by match"
      testid="engagement-by-match"
    >
      <table className="hidden w-full sm:table">
        <thead>
          <tr className="border-b border-rule">
            <th className={TH}>Match</th>
            <th className={TH}>Date</th>
            <th className={TH}>Assigned</th>
            <th className={TH}>Reviewed</th>
            <th className={TH}>Complete</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={TD} colSpan={5}>
                No matches have assigned feedback for these filters.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr
                key={r.match_id}
                className="border-b border-rule/50"
              >
                <td className={`${TD} font-display`}>
                  {r.label || r.match_id}
                </td>
                <td className={TD}>{r.date || "—"}</td>
                <td className={TD}>{r.assigned_count}</td>
                <td className={TD}>{r.reviewed_count}</td>
                <td className={TD}>{r.completion_percentage}%</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="flex flex-col gap-2 sm:hidden">
        {rows.length === 0 ? (
          <p className="text-sm text-ink-muted">No matches have assigned feedback for these filters.</p>
        ) : (
          rows.map((r) => (
            <EngagementMobileRow
              key={r.match_id}
              title={r.label || r.match_id}
              stats={[
                ["Assigned", r.assigned_count],
                ["Reviewed", r.reviewed_count],
                ["Complete", `${r.completion_percentage}%`],
              ]}
              footer={r.date || "No date"}
            />
          ))
        )}
      </div>
    </BreakdownCard>
  );
}

function EngagementMobileRow({
  title,
  stats,
  footer,
}: {
  title: string;
  stats: Array<[string, string | number]>;
  footer: string;
}) {
  return (
    <article className="rounded border border-rule/60 bg-surface px-2 py-2">
      <h4 className="font-display text-sm text-ink">{title}</h4>
      <div className="mt-2 grid grid-cols-2 gap-1">
        {stats.map(([label, value]) => (
          <div key={label} className="rounded bg-bg-card/60 px-2 py-1">
            <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
              {label}
            </span>
            <span className="font-mono text-sm text-ink">{value}</span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[0.65rem] font-mono text-ink-muted">{footer}</p>
    </article>
  );
}

// ---- Follow-up lists ----

function FollowUpList(props: {
  title: string;
  testid: string;
  emptyLabel: string;
  items: ReadonlyArray<{ key: string; primary: string; meta: string }>;
}) {
  return (
    <section
      className="rounded-lg border border-rule bg-bg-card p-3"
      data-testid={props.testid}
    >
      <h3 className="text-sm font-display text-ink mb-2">
        {props.title}
      </h3>
      {props.items.length === 0 ? (
        <p className="text-ink-muted text-xs">{props.emptyLabel}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {props.items.map((item) => (
            <li
              key={item.key}
              className="flex flex-col gap-0.5 rounded border border-rule/60 bg-surface px-2 py-1.5"
            >
              <span className="text-sm text-ink truncate">
                {item.primary}
              </span>
              <span className="text-ink-muted text-xs truncate">
                {item.meta}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
